import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Dumbbell, PlayCircle, TrendingUp, MessageCircle, Calendar, Smartphone, ArrowRight, Check, Apple, Watch, Scale, HeartPulse, Footprints, Activity, Moon, Bike, Flame, Puzzle, Shield, Zap, Heart, Triangle, Youtube, Calendar as CalendarIcon, ClipboardCheck, ClipboardList, MessagesSquare, Sprout } from 'lucide-react';
import { Logo } from '../../Logo';
const TRAINERIZE_URL = 'https://www.trainerize.me/profile/structured/?planGUID=0adc7cc9d3274ca183f4a7ca2a0e16a3';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
// Phone mockup component — stylized "Structured" workout UI
function PhoneMockup() {
  return <div className="relative mx-auto w-[280px] md:w-[320px]" data-id="element-1991">
      {/* Phone frame */}
      <div className="relative bg-charcoal rounded-[3rem] p-3 shadow-2xl border-[3px] border-darkgray" data-id="element-1992">
        {/* Notch */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-32 h-6 bg-charcoal rounded-b-2xl z-20" data-id="element-1993" />

        {/* Screen */}
        <div className="bg-bodybg rounded-[2.3rem] overflow-hidden aspect-[9/19.5] relative" data-id="element-1994">
          {/* Status bar */}
          <div className="flex justify-between items-center px-6 pt-4 pb-2 text-[10px] text-white" data-id="element-1995">
            <span className="font-bold" data-id="element-1996">9:41</span>
            <span className="opacity-70" data-id="element-1997">●●●●●</span>
          </div>

          {/* App content */}
          <div className="px-5 py-4" data-id="element-1998">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 pt-4" data-id="element-1999">
              <Logo className="w-7 h-7" data-id="element-2000" />
              <span className="text-[8px] font-bold text-[#9A9A90] uppercase tracking-[0.25em]" data-id="element-2001">
                Structured
              </span>
            </div>

            {/* Greeting */}
            <p className="text-[10px] text-tealslate uppercase tracking-widest mb-1 font-bold" data-id="element-2002">
              Today's Workout
            </p>
            <h3 className="text-xl font-serif text-white leading-tight mb-1" data-id="element-2003">
              Upper Body
            </h3>
            <p className="text-[10px] text-[#9CA3AF] mb-5" data-id="element-2004">
              Week 3 · Day 2 · 45 min
            </p>

            {/* Progress bar */}
            <div className="bg-darkgray h-1 rounded-full overflow-hidden mb-6" data-id="element-2005">
              <div className="bg-tealslate h-full w-[35%] rounded-full" data-id="element-2006" />
            </div>

            {/* Exercise list */}
            <div className="space-y-2" data-id="element-2007">
              {[{
              name: 'Incline DB Press',
              sets: '4 × 8',
              done: true
            }, {
              name: 'Cable Row',
              sets: '4 × 10',
              done: true
            }, {
              name: 'Lat Pulldown',
              sets: '3 × 12',
              done: false,
              active: true
            }, {
              name: 'Lateral Raise',
              sets: '3 × 15',
              done: false
            }, {
              name: 'Tricep Pushdown',
              sets: '3 × 12',
              done: false
            }].map((ex, i) => <div key={i} className={`flex items-center gap-3 p-2.5 rounded ${ex.active ? 'bg-tealslate/20 border-l-2 border-tealslate' : ex.done ? 'bg-navyslate/30 opacity-60' : 'bg-navyslate/30'}`} data-id="element-2008">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${ex.done ? 'bg-tealslate' : 'border border-[#9CA3AF]'}`} data-id="element-2009">
                    {ex.done && <Check className="w-3 h-3 text-white" data-id="element-2010" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2011">
                    <p className="text-[10px] font-bold text-white truncate" data-id="element-2012">
                      {ex.name}
                    </p>
                    <p className="text-[8px] text-[#9CA3AF]" data-id="element-2013">{ex.sets}</p>
                  </div>
                  {ex.active && <PlayCircle className="w-4 h-4 text-tealslate shrink-0" data-id="element-2014" />}
                </div>)}
            </div>

            {/* CTA at bottom */}
            <div className="mt-5 bg-tealslate text-center py-3 rounded" data-id="element-2015">
              <p className="text-[10px] font-bold text-white uppercase tracking-widest" data-id="element-2016">
                Continue Workout
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Glow effect */}
      <div className="absolute inset-0 bg-tealslate/10 blur-3xl -z-10" data-id="element-2017" />
    </div>;
}
export function Training() {
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-2018">
      {/* VIDEO HERO */}
      <section className="relative w-full overflow-hidden bg-black pt-20" data-id="element-2019">
        <div className="relative w-full aspect-video max-h-[80vh] mx-auto" data-id="element-2020">
          <video src="https://file.trainerize.com/assets/tz/home/videos/app-features-v2.mp4" autoPlay muted loop playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" data-id="element-2021" />
          {/* Dark overlay for text legibility */}
          <div className="absolute inset-0 bg-black/60 pointer-events-none" data-id="element-2022" />

          {/* Overlay content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-2023">
            <motion.h2 initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.1
          }} className="text-4xl md:text-6xl lg:text-7xl font-serif text-white leading-[1.05] max-w-4xl" data-id="element-2024">
              Train smarter. Anywhere.
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="text-base md:text-lg text-white/85 max-w-xl mt-5 leading-relaxed" data-id="element-2025">
              A coach in your pocket — workouts, tracking, and progress, all in
              one app.
            </motion.p>
          </div>
        </div>
      </section>

      {/* VALUE PROPS STRIP */}
      <section className="bg-[#F5F5F0] py-14 md:py-16 px-6" data-id="element-2026">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8 text-center" data-id="element-2027">
          {[{
          icon: ClipboardList,
          title: 'Programmed Workouts',
          desc: 'Training tailored to your goals and schedule.'
        }, {
          icon: TrendingUp,
          title: 'Evidence-Based',
          desc: 'Built on exercise science, not gym myths.'
        }, {
          icon: HeartPulse,
          title: 'Sustainable Progress',
          desc: 'Strength gains that outlast the program.'
        }, {
          icon: MessagesSquare,
          title: 'Coach Support',
          desc: 'Direct access to a coach who answers.'
        }].map((item, i) => <div key={i} className="flex flex-col items-center" data-id="element-2028">
              <item.icon className="w-6 h-6 text-[#1F1F1F] mb-4" strokeWidth={1.5} data-id="element-2029" />
              <h3 className="text-lg font-serif font-bold text-[#1F1F1F] mb-2" data-id="element-2030">
                {item.title}
              </h3>
              <p className="text-sm text-tealslate leading-relaxed max-w-[200px]" data-id="element-2031">
                {item.desc}
              </p>
            </div>)}
        </div>
      </section>

      {/* HERO */}
      <section className="bg-bodybg pt-16 pb-10 md:pb-14 px-6 border-b border-darkgray relative overflow-hidden" data-id="element-2032">
        {/* Background image — dark grunge texture */}
        <div className="absolute inset-0 bg-cover bg-center pointer-events-none" style={{
        backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/jnhsHXXgzHt8i7RDXcnHjm/pasted-image.jpg)'
      }} data-id="element-2033" />
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-tealslate/5 rounded-full blur-3xl pointer-events-none" data-id="element-2034" />

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-2035">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2036">
            {/* Left: Pitch */}
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} data-id="element-2037">
              <motion.h1 variants={fadeUpVariants} className="text-4xl md:text-6xl font-serif text-white leading-[1.05] mb-6" data-id="element-2038">
                Structured training. Built for the way you live now.
              </motion.h1>
              <motion.div variants={fadeUpVariants} className="w-12 h-0.5 bg-tealslate mb-6" data-id="element-2039" />
              <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#D8D8D0] leading-relaxed mb-10 max-w-xl" data-id="element-2040">
                Programmed workouts designed to preserve muscle, build strength,
                and maximize the results of your GLP-1 journey — delivered
                through a coach-built training app.
              </motion.p>

              {/* Price */}
              <motion.div variants={fadeUpVariants} className="flex items-baseline gap-3 mb-8" data-id="element-2041">
                <span className="text-5xl md:text-6xl font-serif text-white" data-id="element-2042">
                  $6.99
                </span>
                <span className="text-sm text-[#9CA3AF] uppercase tracking-widest" data-id="element-2043">
                  / month
                </span>
              </motion.div>

              {/* CTAs */}
              <motion.div variants={fadeUpVariants} className="flex flex-col sm:flex-row gap-4" data-id="element-2044">
                <a href={TRAINERIZE_URL} target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-lightteal transition-colors text-center inline-flex items-center justify-center gap-2" data-id="element-2045">
                  Start Your Program <ArrowRight className="w-4 h-4" data-id="element-2046" />
                </a>
                <a href="#whats-included" className="px-8 py-4 border border-[#D8D8D0] text-[#D8D8D0] text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors text-center" data-id="element-2047">
                  What's Included
                </a>
              </motion.div>

              <motion.p variants={fadeUpVariants} className="text-xs text-[#7A7A70] italic mt-6" data-id="element-2048">
                Cancel anytime · No long-term commitment
              </motion.p>
            </motion.div>

            {/* Right: Phone mockup */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            y: 30
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.3,
            ease: [0.2, 0.65, 0.3, 0.9]
          }} className="flex justify-center" data-id="element-2049">
              <img src="https://cdn.magicpatterns.com/uploads/5rm9yb4s15WyVS6g5EYh3Q/pasted-image.webp" alt="Structured training program in the ABC Trainerize app" className="w-[280px] md:w-[340px] h-auto drop-shadow-2xl" data-id="element-2050" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* MEAL TRACKING VISUAL */}
      <section className="bg-white py-16 md:py-24 px-6" data-id="element-2051">
        <div className="max-w-7xl mx-auto flex justify-center" data-id="element-2052">
          <img src="https://cdn.magicpatterns.com/uploads/6WN21WJ1kjcjz47Ue6HQVC/image.png" alt="Meal tracking inside the Structured training app — photo food journaling, full nutrition tracking, and custom recipes" className="w-full h-auto" data-id="element-2053" />
        </div>
      </section>

      {/* INTEGRATIONS — LEVEL UP YOUR PROGRESS */}
      <section className="bg-[#F7F8FB] py-10 md:py-14 px-6" data-id="element-2054">
        <div className="max-w-7xl mx-auto" data-id="element-2055">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-2056">
            {/* Left */}
            <div data-id="element-2057">
              <p className="text-xs md:text-sm font-bold text-tealslate uppercase tracking-[0.25em] mb-6" data-id="element-2058">
                Integrations
              </p>
              <h2 className="text-5xl md:text-6xl font-bold text-[#0B1B3A] leading-[1.02] tracking-tight mb-6" data-id="element-2059">
                Level up your <span className="text-tealslate" data-id="element-2060">progress.</span>
              </h2>
              <p className="text-base md:text-lg text-[#5A6478] leading-relaxed max-w-md mb-8" data-id="element-2061">
                Effortlessly connect ABC Trainerize with the world's most
                powerful apps to level up your fitness and track what matters
                most.
              </p>
              <a href={TRAINERIZE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-7 py-4 bg-tealslate text-white text-sm font-bold hover:bg-[#2A3A3F] transition-colors rounded-md" data-id="element-2062">
                See all integrations <ArrowRight className="w-4 h-4" data-id="element-2063" />
              </a>
            </div>

            {/* Right: integration tile grid */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 md:gap-4" data-id="element-2064">
              {[{
              label: 'Apple',
              icon: <Apple className="w-8 h-8 text-black" data-id="element-2065" />,
              bg: 'bg-white'
            }, {
              label: 'Health Connect',
              icon: <Heart className="w-7 h-7 text-white fill-white" data-id="element-2066" />,
              bg: 'bg-[#5B6BF5]'
            }, {
              label: 'Prism',
              icon: <Triangle className="w-8 h-8 text-black fill-black" data-id="element-2067" />,
              bg: 'bg-white'
            }, {
              label: 'Fitbit',
              icon: <div className="grid grid-cols-3 gap-0.5" data-id="element-2068">
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(i => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === 4 ? 'bg-transparent' : 'bg-[#00B0B9]'}`} data-id="element-2069" />)}
                    </div>,
              bg: 'bg-white'
            }, {
              label: 'Zapier',
              icon: <span className="text-[#FF4F00] font-bold text-base italic" data-id="element-2070">
                      zapier
                    </span>,
              bg: 'bg-white'
            }, {
              label: 'YouTube',
              icon: <Youtube className="w-9 h-9 text-[#FF0000] fill-[#FF0000]" data-id="element-2071" />,
              bg: 'bg-white'
            }, {
              label: 'Growth',
              icon: <span className="text-white font-extrabold text-xl" data-id="element-2072">G</span>,
              bg: 'bg-[#1FB07A]'
            }, {
              label: 'TrueCoach',
              icon: <span className="text-[#00C896] font-extrabold text-xl" data-id="element-2073">
                      O
                    </span>,
              bg: 'bg-black'
            }, {
              label: 'MyFitnessPal',
              icon: <span className="text-white font-bold text-xl italic" data-id="element-2074">
                      MFP
                    </span>,
              bg: 'bg-[#0072CE]'
            }, {
              label: 'Trainerize Me',
              icon: <Heart className="w-7 h-7 text-[#2563EB] fill-[#2563EB]" data-id="element-2075" />,
              bg: 'bg-white'
            }, {
              label: 'Apple Health',
              icon: <Heart className="w-7 h-7 text-[#FF2D55] fill-[#FF2D55]" data-id="element-2076" />,
              bg: 'bg-white'
            }, {
              label: 'Stripe',
              icon: <span className="text-white font-bold text-xl italic" data-id="element-2077">
                      S
                    </span>,
              bg: 'bg-[#635BFF]'
            }, {
              label: 'Google Calendar',
              icon: <span className="text-[#4285F4] font-bold text-xl" data-id="element-2078">31</span>,
              bg: 'bg-white'
            }, {
              label: 'Acuity Scheduling',
              icon: <span className="text-white font-bold text-xl" data-id="element-2079">A</span>,
              bg: 'bg-[#1FB6FF]'
            }].map((item, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-2080">
                  <div className={`w-full aspect-square rounded-2xl shadow-sm border border-[#EAECF1] flex items-center justify-center ${item.bg} mb-2`} data-id="element-2081">
                    {item.icon}
                  </div>
                  <span className="text-[10px] md:text-[11px] text-[#5A6478] leading-tight" data-id="element-2082">
                    {item.label}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Feature row */}
          <div className="mt-16 md:mt-20 bg-white border border-[#EAECF1] rounded-2xl p-8 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12" data-id="element-2083">
            {[{
            icon: Puzzle,
            title: 'Seamless Connections',
            desc: 'Integrations that sync your data and save you time.'
          }, {
            icon: Shield,
            title: 'Trusted Partners',
            desc: 'Connect with industry-leading apps you already use.'
          }, {
            icon: Zap,
            title: 'Track What Matters',
            desc: 'Unify your data, stay consistent, and reach your goals faster.'
          }].map((f, i) => <div key={i} className="flex items-start gap-4" data-id="element-2084">
                <div className="w-12 h-12 rounded-full bg-tealslate/10 flex items-center justify-center flex-shrink-0" data-id="element-2085">
                  <f.icon className="w-5 h-5 text-tealslate" data-id="element-2086" />
                </div>
                <div data-id="element-2087">
                  <h3 className="text-base font-bold text-[#0B1B3A] mb-1" data-id="element-2088">
                    {f.title}
                  </h3>
                  <p className="text-sm text-[#5A6478] leading-relaxed" data-id="element-2089">
                    {f.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* FINAL CTA — Free Trial Email Capture */}
      <section className="relative bg-black py-24 md:py-32 px-6 text-center overflow-hidden" data-id="element-2090">
        {/* Subtle diagonal accent stripes */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(115deg, transparent 0 220px, #ffffff 220px 320px, transparent 320px 540px)'
      }} data-id="element-2091" />
        <div className="relative max-w-3xl mx-auto" data-id="element-2092">
          <h2 className="text-4xl md:text-6xl font-normal text-white leading-tight tracking-tight mb-6" data-id="element-2093">
            Start your <span className="text-[#FACC15]" data-id="element-2094">free 30-day trial</span>
          </h2>
          <p className="text-sm md:text-base font-bold text-white/90 uppercase tracking-[0.2em] mb-10" data-id="element-2095">
            And build muscle with a plan.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch max-w-2xl mx-auto border-2 border-[#FACC15] overflow-hidden" data-id="element-2096">
            <div className="flex-1 bg-black text-white px-6 py-4 text-base tracking-[0.2em] uppercase flex items-center justify-center sm:justify-start" data-id="element-2097">
              Use Code: ONEMONTH
            </div>
            <a href={TRAINERIZE_URL} target="_blank" rel="noopener noreferrer" className="bg-[#FACC15] hover:bg-[#EAB308] text-black text-base font-bold px-8 py-4 transition-colors whitespace-nowrap inline-flex items-center justify-center" data-id="element-2098">
              Get started
            </a>
          </div>

          <p className="text-sm text-white/70 italic mt-6" data-id="element-2099">
            Enjoy 30 days free with unlimited access.
          </p>
        </div>
      </section>
    </div>;
}